/**
 * 寄语信箱 API 服务
 * 用于与OpenClaw后端API交互
 */

import axios from 'axios';

// API基础URL配置 - 使用相对路径以支持手机访问
const API_BASE_URL = process.env.REACT_APP_OPENCLAW_API_URL || '';
const API_PREFIX = 'openclawapi';

// 创建axios实例
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60秒超时
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    console.log('API请求:', config);
    return config;
  },
  (error) => {
    console.error('API请求错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    console.log('API响应:', response);
    return response;
  },
  (error) => {
    console.error('API响应错误:', error);
    return Promise.reject(error);
  }
);

// 类型定义
export interface QRCodeRequest {
  timeout?: number;
  add_text?: boolean;
  agent_id?: string;
}

export interface QRCodeResponse {
  qr_id: string;
  qr_url: string;
  qr_image_url: string;
  status: 'pending' | 'scanning' | 'confirmed' | 'connected' | 'expired' | 'failed';
  expires_at: string;
  created_at: string;
  timeout: number;
}

export interface WeChatAccount {
  account_id: string;
  user_id: string;
  created_at: string;
  last_active?: string;
  agent_id?: string;
  status: 'connected' | 'disconnected';
}

export interface ServiceStatus {
  service: string;
  version: string;
  status: string;
  uptime?: number;
  openclaw_installed: boolean;
  wechat_plugin_enabled: boolean;
  active_sessions: number;
  connected_accounts: number;
}

export interface APIResponse {
  success: boolean;
  message: string;
  data?: any;
  error_code?: string;
}

// 二维码API
export const qrcodeApi = {
  // 创建二维码
  create: async (request: QRCodeRequest): Promise<QRCodeResponse> => {
    const response = await api.post<QRCodeResponse>(
      `${API_PREFIX}/qrcode`,
      request
    );
    return response.data;
  },

  // 获取二维码状态
  getStatus: async (qrId: string): Promise<QRCodeResponse> => {
    const response = await api.get<QRCodeResponse>(
      `${API_PREFIX}/qrcode/${qrId}`
    );
    return response.data;
  },

  // 获取二维码图片URL
  getImageUrl: (qrId: string): string => {
    return `${API_PREFIX}/qrcode/${qrId}/image`;
  },

  // 获取二维码重定向URL
  getRedirectUrl: (qrId: string): string => {
    return `${API_PREFIX}/qrcode/${qrId}/redirect`;
  },

  // 删除二维码
  delete: async (qrId: string): Promise<APIResponse> => {
    const response = await api.delete<APIResponse>(
      `${API_PREFIX}/qrcode/${qrId}`
    );
    return response.data;
  },

  // 获取所有二维码
  getAll: async (status?: string, limit: number = 50): Promise<QRCodeResponse[]> => {
    const params: any = { limit };
    if (status) {
      params.status = status;
    }
    const response = await api.get<QRCodeResponse[]>(
      `${API_PREFIX}/qrcodes`,
      { params }
    );
    return response.data;
  },

  // 配置账号到agent
  configureAccount: async (accountId: string, agentId?: string, channel?: string): Promise<APIResponse> => {
    const response = await api.post<APIResponse>(
      `${API_PREFIX}/configure-account`,
      {
        account_id: accountId,
        agent_id: agentId || 'wechat-assistant',
        channel: channel || 'openclaw-weixin'
      }
    );
    return response.data;
  },
};

// 账号API
export const accountsApi = {
  // 获取所有账号
  getAll: async (): Promise<WeChatAccount[]> => {
    const response = await api.get<WeChatAccount[]>(
      `${API_PREFIX}/accounts`
    );
    return response.data;
  },

  // 获取账号详情
  getDetail: async (accountId: string): Promise<WeChatAccount> => {
    const response = await api.get<WeChatAccount>(
      `${API_PREFIX}/accounts/${accountId}`
    );
    return response.data;
  },

  // 删除账号
  delete: async (accountId: string): Promise<APIResponse> => {
    const response = await api.delete<APIResponse>(
      `${API_PREFIX}/accounts/${accountId}`
    );
    return response.data;
  },
};

// 状态API
export const statusApi = {
  // 获取服务状态
  getServiceStatus: async (): Promise<ServiceStatus> => {
    const response = await api.get<ServiceStatus>(
      `${API_PREFIX}/status`
    );
    return response.data;
  },

  // 获取OpenClaw状态
  getOpenClawStatus: async (): Promise<any> => {
    const response = await api.get<any>(
      `${API_PREFIX}/openclaw/status`
    );
    return response.data;
  },

  // 健康检查
  healthCheck: async (): Promise<any> => {
    const response = await api.get<any>(
      `${API_PREFIX}/health`
    );
    return response.data;
  },
};

// OpenClaw 管理API
export const openclawApi = {
  // 绑定 agent
  bindAgent: async (accountId: string, agentId: string = 'wechat-receiver'): Promise<APIResponse> => {
    const response = await api.post<APIResponse>(
      `${API_PREFIX}/agents/bind`,
      {
        account_id: accountId,
        agent_id: agentId
      }
    );
    return response.data;
  },

  // 重启网关
  restartGateway: async (): Promise<APIResponse> => {
    const response = await api.post<APIResponse>(
      `${API_PREFIX}/gateway/restart`
    );
    return response.data;
  },
};

export default {
  qrcodeApi,
  accountsApi,
  statusApi,
  openclawApi,
};
